import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "hooks/useAppDispatch";
import { isCanceled, makeCancelable } from "lib/axios";
import { subscribeToPushNotifications } from "lib/pushNotifications";
import { show } from "store/reducers/notifications.reducer";
import {
  buildCalendarEventIdentity,
  getCalendarEventSubscriptions,
  subscribeToCalendarEvent,
  unsubscribeFromCalendarEvent,
} from "lib/economicCalendarEventNotifications";

const indexSubscriptions = (items) => {
  const result = new Map();
  items.forEach((item) => {
    result.set(buildCalendarEventIdentity(item), item);
  });
  return result;
};

const useEconomicCalendarEventNotifications = (date) => {
  const { t } = useTranslation("economicCalendar");
  const dispatch = useAppDispatch();
  const [subscriptions, setSubscriptions] = useState(() => new Map());
  const [updating, setUpdating] = useState(() => new Set());
  const [loading, setLoading] = useState(Boolean(date));
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    if (!date) {
      setSubscriptions(new Map());
      setLoading(false);
      return undefined;
    }

    const cancelable = makeCancelable();
    let active = true;
    setLoading(true);
    setAvailable(true);

    getCalendarEventSubscriptions(date, cancelable)
      .then((items) => {
        if (active) setSubscriptions(indexSubscriptions(items));
      })
      .catch((error) => {
        if (!active || isCanceled(error)) return;
        setSubscriptions(new Map());
        setAvailable(false);
        console.error("Could not load economic calendar event alerts", error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      cancelable.cancel();
    };
  }, [date]);

  const toggle = useCallback(
    async (event) => {
      const identity = buildCalendarEventIdentity(event);
      const current = subscriptions.get(identity);
      if (updating.has(identity)) return;

      setUpdating((previous) => new Set(previous).add(identity));
      try {
        if (current?.eventKey) {
          await unsubscribeFromCalendarEvent(current.eventKey);
          setSubscriptions((previous) => {
            const next = new Map(previous);
            next.delete(identity);
            return next;
          });
          dispatch(
            show({
              type: "success",
              msg: t("eventNotifications.disabled"),
            })
          );
        } else {
          let pushReady = false;
          let pushWarningKey = "savedTechnicalError";
          try {
            const pushResult = await subscribeToPushNotifications();
            pushReady = pushResult.ok;
            if (!pushReady) {
              if (pushResult.reason === "dismissed") {
                dispatch(
                  show({
                    type: "info",
                    msg: t("eventNotifications.permissionDismissed"),
                  })
                );
                return;
              }
              if (
                pushResult.permission === "denied" ||
                pushResult.reason === "denied"
              ) {
                pushWarningKey = "savedBrowserBlocked";
              } else if (pushResult.reason === "unsupported") {
                pushWarningKey = "savedUnsupported";
              } else if (pushResult.reason === "missing-config") {
                pushWarningKey = "savedUnavailable";
              }
            }
          } catch (pushError) {
            console.error("Could not enable push on this device", pushError);
          }

          const item = await subscribeToCalendarEvent(event);
          if (item) {
            setSubscriptions((previous) => {
              const next = new Map(previous);
              next.set(buildCalendarEventIdentity(item), item);
              return next;
            });
            dispatch(
              show({
                type: pushReady ? "success" : "warning",
                msg: pushReady
                  ? t("eventNotifications.enabled", {
                      minutes: item.minutesBefore,
                    })
                  : t(`eventNotifications.${pushWarningKey}`, {
                      minutes: item.minutesBefore,
                    }),
              })
            );
          }
        }
        setAvailable(true);
      } catch (error) {
        console.error("Could not update economic calendar event alert", error);
        dispatch(
          show({
            type: "error",
            msg: t("eventNotifications.error"),
          })
        );
      } finally {
        setUpdating((previous) => {
          const next = new Set(previous);
          next.delete(identity);
          return next;
        });
      }
    },
    [dispatch, subscriptions, t, updating]
  );

  const isSubscribed = useCallback(
    (event) => subscriptions.has(buildCalendarEventIdentity(event)),
    [subscriptions]
  );
  const isUpdating = useCallback(
    (event) => updating.has(buildCalendarEventIdentity(event)),
    [updating]
  );

  return { available, isSubscribed, isUpdating, loading, toggle };
};

export default useEconomicCalendarEventNotifications;
